import { ErrorCode } from "react-dropzone";

import type { Connector, ConnectorContent, ConnectorWithBrain } from "./connector";
import type { Folder } from "./folder";
import type { IntegrationStatus } from "./integration";
import {
	CONTENT_MIME_TYPES,
	type ContentMimeType,
	IMAGE_JPEG_MIME_TYPE,
	IMAGE_PNG_MIME_TYPE,
	SOPHOS_CONTENT_MIME_TYPES,
	type SophosContentMimeType,
} from "./mimeTypes";
import type { Vector } from "./vector";

export const errorMessages = {
	[ErrorCode.FileInvalidType]: "File type not accepted, sorry!",
	[ErrorCode.FileTooLarge]: `File is too big, max size is 250 Mb`,
	[ErrorCode.FileTooSmall]: "File is too small",
	[ErrorCode.TooManyFiles]: "Too many files",
};

export const errorCodes: Record<ErrorCode[number], ContentUploadErrorCode> = {
	[ErrorCode.FileInvalidType]: "content-type-not-supported",
	[ErrorCode.FileTooLarge]: "file-too-big",
	[ErrorCode.FileTooSmall]: "empty-content",
	[ErrorCode.TooManyFiles]: "internal-error",
};

export const commercialType = [
	"proposal",
	"statement-of-work",
	"engagement-letter",
	"pricing-fee-schedule",
	"capability-deck",
	"case-study",
] as const;
export const contractualType = [
	"nda",
	"master-services-agreement",
	"amendment-addendum",
	"subcontractor-agreement",
	"data-processing-agreement",
] as const;
export const projectManagementType = [
	"project-plan",
	"status-report",
	"steering-committee-pack",
	"raci-matrix",
	"risk-issue-log",
	"decision-log",
	"action-tracker",
	"change-request",
	"weekly-meeting-support",
] as const;
export const workingDocumentType = [
	"interview-notes",
	"workshop-output",
	"data-extract",
	"analysis-workbook",
	"financial-model",
	"benchmark-table",
	"desk-research-notes",
	"assumptions-log",
] as const;
export const deliverablesType = [
	"report",
	"slide-deck",
	"dashboard-scorecard",
	"process-map",
	"roadmap",
	"playbook-handbook",
	"policy-document",
] as const;
export const knowledgeType = [
	"template",
	"methodology-guide",
	"training-material",
	"best-practice-library",
	"reusable-framework",
] as const;
export const administrativeType = [
	"timesheet",
	"invoice",
	"expense-report",
	"budget-tracker",
	"resource-plan",
] as const;
export const clientInputTypes = [
	"client-brief-rfp",
	"source-data",
	"feedback-validation-comments",
] as const;
export const communicationTypes = [
	"meeting-minutes",
	"email-thread",
	"memo-note-to-file",
	"transcript",
] as const;
export const otherTypes = ["article", "cv", "guideline", "financials", "other"] as const;

export const contentType = [
	...commercialType,
	...contractualType,
	...projectManagementType,
	...workingDocumentType,
	...deliverablesType,
	...knowledgeType,
	...administrativeType,
	...clientInputTypes,
	...communicationTypes,
	...otherTypes,
] as const;

export type ContentType = (typeof contentType)[number];

export const contentUploadStatus = ["waiting", "processing", "processed", "failed"] as const;

export type ContentUploadStatus = (typeof contentUploadStatus)[number];

export const contentProcessingSteps = [
	"validating",
	"extracting",
	"indexing",
	"vectorizing",
	"summarizing",
] as const;

export type ContentProcessingStep = (typeof contentProcessingSteps)[number];

export const contentUploadStatusToIntegrationStatus: Record<
	ContentUploadStatus,
	IntegrationStatus
> = {
	waiting: "initializing",
	processing: "discovering",
	processed: "discovered",
	failed: "deleting",
};

export const contentSource = [
	"web",
	"upload",
	"youtube",
	"integration",
	"document-template",
	"generated-document",
	"deliverable",
] as const;

export type ContentSource = (typeof contentSource)[number];

export const contentUploadErrorCode = [
	"brain-is-full",
	"connection-error",
	"content-corrupt",
	"content-type-not-supported",
	"empty-content",
	"file-too-big",
	"internal-error",
	"invalid-file-type",
	"invalid-url",
	"invalid-youtube-link",
	"parsing-error",
	"protected-content",
	"summary-generation-error",
	"timed-out",
	"video-no-transcript",
	"wrong-password",
	"wrong-response",
	"file-not-changed",
	"malware-detected",
	// text-extract dependency failures (see apps/text-extract/app/utils/exceptions.py)
	"llm-provider-error",
	"s3-error",
	"docling-error",
	"youtube-invalid-url",
	"unreadable-text-layer",
] as const;

export type ContentUploadErrorCode = (typeof contentUploadErrorCode)[number];

export type IUploadContentError = {
	error: {
		message: string;
		name: ContentUploadErrorCode;
		data?: Record<string, unknown> | undefined;
	};
};

export interface AddContentError {
	name: ContentUploadErrorCode;
	message: string;
	data?:
		| {
				contentType?: string | undefined;
				statusError?: string | undefined;
		  }
		| undefined;
}

export interface ContentBaseMetadata {
	date?: string;
	version?: string;
	author?: string;
	language?: string;
	timeToLoad?: number;
}

export interface ContentWebMetadata extends ContentBaseMetadata {
	awsKey?: string | undefined;
}

export interface ContentUploadMetadata extends ContentBaseMetadata {
	awsKey: string;
	password?: string | undefined;
}

export interface ContentYoutubeMetadata extends ContentBaseMetadata {
	youtubeId: string;
}

export interface ContentIntegrationMetadata extends ContentBaseMetadata {
	awsKey?: string | undefined;
}

export interface ContentDocumentTemplateMetadata {
	awsKey?: string | undefined;
}

export interface ContentGeneratedDocumentMetadata {
	awsKey: string;
}

export interface ContentDeliverableMetadata {
	awsKey: string;
	artifactId?: string;
	chatId?: string;
	sourceType?: "artifact" | "block-message" | "session";
	sourceId?: string;
}

export interface ContentMetadataMap {
	web: ContentWebMetadata;
	youtube: ContentYoutubeMetadata;
	upload: ContentUploadMetadata;
	integration: ContentIntegrationMetadata;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"document-template": ContentDocumentTemplateMetadata;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	"generated-document": ContentGeneratedDocumentMetadata;
	deliverable: ContentDeliverableMetadata;
}

export type ContentMetadata<T extends ContentSource = ContentSource> = ContentMetadataMap[T];

export type Content = {
	[K in ContentSource]: BaseContent<K>;
}[ContentSource];

interface BaseContent<T extends ContentSource = ContentSource> {
	id: string;
	title: string;
	source: T;
	mimeType: ContentMimeType | "video";
	type?: string | undefined;
	keywords?: string[] | undefined;
	url: string;
	hash: string | undefined;
	index?: number | undefined;

	displayed: boolean;
	size: number;
	metadata: ContentMetadataMap[T];

	uploadStatus: ContentUploadStatus;
	uploadErrorCode?: ContentUploadErrorCode | undefined;

	connectors?: Connector[] | undefined;
	folders?: Folder[] | undefined;
	connectorContents?: ConnectorContent[] | undefined;

	vectors?: Vector[] | undefined;

	createdAt: Date;
	updatedAt: Date;
	lastUpdatedAt: Date;
}

export type ContentWithBrains<T extends ContentSource = ContentSource> = Omit<
	BaseContent<T>,
	"connectors"
> & {
	connectors: ConnectorWithBrain[];
};

export type ContentWithBrainsAndVectors<T extends ContentSource = ContentSource> = Omit<
	BaseContent<T>,
	"connectors"
> & {
	connectors: ConnectorWithBrain[];
	vectors: Vector[];
};

export interface ContentTokenStats {
	id: string;
	mimeType: ContentMimeType;
	tokenSize: number;
	averagePerChunk: number;
}

export interface DocumentTitle {
	id: string;
	brainId?: string | undefined;
	title: string;
	source: ContentSource;
	mimeType: ContentMimeType | "video" | "folder" | "link" | "file" | "drive" | "workspace";
}

export type MandatoryContentFields = Pick<
	Content,
	"title" | "source" | "mimeType" | "size" | "metadata"
> &
	Partial<Content>;

export type ContentUsage = {
	processingContents: number;
	failedContents: number;
	waitingContents: number;
	processedContents: number;
	onHoldContents: number;
	totalContents: number;
	usersBrainsCount: number;
	errorCounts: ErrorCount[];
	uploadedContents?: number | undefined;
};

export interface ContentsContext {
	mimeTypes: Array<{
		mimeType: ContentMimeType;
		total: string;
	}>;
	types: Array<{
		type: ContentType;
		total: string;
	}>;
	keywords?: string[];
}

type ErrorCount = {
	uploadErrorCode: ContentUploadErrorCode;
	total: number;
};

export interface HttpError {
	response?: {
		status?: number;
		data?: {
			error?: {
				code?: string;
			};
		};
	};
}

export function isHttpError(err: unknown): err is HttpError {
	return typeof err === "object" && err !== null && "response" in err;
}

export function isUploadContent(content: BaseContent): content is BaseContent<"upload"> {
	return content.source === "upload";
}

export function isYoutubeContent(content: BaseContent): content is BaseContent<"youtube"> {
	return content.source === "youtube";
}

export function isWebContent(content: BaseContent): content is BaseContent<"web"> {
	return content.source === "web";
}

export function isIntegrationContent(content: BaseContent): content is BaseContent<"integration"> {
	return content.source === "integration";
}

export function isDeliverableContent(content: BaseContent): content is BaseContent<"deliverable"> {
	return content.source === "deliverable";
}

export const isValidContentMimeType = (mimeType: string, isIntegration: boolean): boolean =>
	(mimeType === IMAGE_PNG_MIME_TYPE || mimeType === IMAGE_JPEG_MIME_TYPE) && isIntegration
		? false
		: CONTENT_MIME_TYPES.includes(mimeType as ContentMimeType);

export const isValidContentSophosMimeType = (sophosMimeType: string): boolean =>
	SOPHOS_CONTENT_MIME_TYPES.includes(sophosMimeType as SophosContentMimeType);

export interface AddToBrainParams {
	brainId: string;
	type: "artifact" | "block-message" | "session";
	id: string;
}
